import React from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './shop-panel-with-inset-image.module.css';
import CtaLink from '../Global/CtaLink';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import {
    CTAConfiguration,
    KontentStringValue,
    KontentRichText,
    KontentMedia,
    KontentNumberValue,
    BrandColor,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { getColor, getMultiChoice } from '../Global/DataUtils';
import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
import { useIsClient } from '../Global/DataUtils/useIsClient';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
} from '../Global/DataUtils';

interface Props extends CTAConfiguration {
    kontentItemId?: string;
    headline: KontentStringValue;
    description: KontentRichText;
    image: KontentMedia;
    constrain_width_of_component: KontentRadioMultiChoice<'yes' | 'no'>;
    component_anchor_name: KontentStringValue;
    content_panel__content_panel_color: BrandColor;
    content_panel__content_panel_font_color: BrandColor;
    image_side: KontentRadioMultiChoice<'left' | 'right'>;
}

const ShopPanelWithInsetImage: React.FC<Props> = ({
    headline,
    description,
    constrain_width_of_component,
    content_panel__content_panel_color,
    content_panel__content_panel_font_color,
    component_anchor_name,
    image_side,
    kontentItemId,
    ...props
}) => {
    const breakpoints = useBreakpoint();
    const isClient = useIsClient();
    const isXl = isClient && breakpoints.xl;

    const ctaLocation = BuildLinkHref(
        props.cta_configuration__cta_location,
        props.cta_configuration__cta_location_anchor_point
    );

    const backgroundColor =
        getColor(content_panel__content_panel_color) || undefined;
    const fontColor =
        getColor(content_panel__content_panel_font_color) || undefined;
    const authorStyles = { backgroundColor, color: fontColor };

    const cta = {
        label: props.cta_configuration__cta_label?.value || 'Shop Now',
        location: ctaLocation,
        target:
            getMultiChoice(props.cta_configuration__cta_window_action) ===
            'new_window'
                ? '_blank'
                : '_self',
    };

    const imageData = getImageData(props.image);
    const constrainWidth =
        getMultiChoice(constrain_width_of_component) === 'yes';

    const contentClass =
        getMultiChoice(image_side) === 'left'
            ? `${styles.content} ${styles.imageOnLeft}`
            : styles.content;

    const renderCopy = () => {
        return (
            <div>
                {description?.value && (
                    <div className={styles.description}>
                        {RichText(
                            description,
                            false,
                            undefined,
                            undefined,
                            'description'
                        )}
                    </div>
                )}
                {cta?.location && (
                    <div className={styles.ctaLink}>
                        <CtaLink
                            href={cta.location}
                            target={cta.target}
                            color={fontColor || '#505251'}
                        >
                            {cta.label}
                        </CtaLink>
                    </div>
                )}
            </div>
        );
    };

    return (
        <section
            className={
                constrainWidth ? styles.constrained : styles.unconstrained
            }
            style={!constrainWidth ? authorStyles : undefined}
            id={component_anchor_name?.value || undefined}
            data-kontent-item-id={kontentItemId}
        >
            <div
                className={contentClass}
                style={constrainWidth ? authorStyles : undefined}
            >
                <div className={styles.copyContainer}>
                    <h2 data-kontent-element-codename="headline">
                        {headline?.value}
                    </h2>
                    <div className={styles.copyDesktopOnly} aria-hidden={!isXl}>
                        {renderCopy()}
                    </div>
                </div>
                {imageData?.image && (
                    <div data-kontent-element-codename="image">
                        <img
                            src={
                                imageData.image
                                    ? optimizeKontentImageUrl(
                                          (imageData.image?.url ||
                                              imageData.image) as string,
                                          {
                                              w: 800,
                                              fit: 'crop',
                                              q: 80,
                                              auto: 'format',
                                          }
                                      )
                                    : ''
                            }
                            srcSet={
                                imageData.image
                                    ? buildKontentSrcSet(
                                          (imageData.image?.url ||
                                              imageData.image) as string,
                                          800,
                                          { fit: 'crop', q: 80, auto: 'format' }
                                      )
                                    : ''
                            }
                            sizes="(max-width: 800px) 100vw, 800px"
                            alt={imageData.alt}
                            width={800}
                            height={undefined}
                            loading="lazy"
                            className={styles.image}
                            style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'cover',
                            }}
                        />
                    </div>
                )}
                <div className={styles.copyMobileOnly} aria-hidden={isXl}>
                    {renderCopy()}
                </div>
            </div>
        </section>
    );
};

export default ShopPanelWithInsetImage;

export const fragmentStatementBlockWithIconOption = graphql`
    fragment ShopPanelWithInsetImage on kontent_item_component___shop_panel_with_inset_image {
        id
        elements {
            headline {
                value
            }
            image {
                ...kontentImage
            }
            description {
                ...kontentRichText
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_label {
                value
            }
            constrain_width_of_component {
                value {
                    codename
                }
            }
            component_anchor_name {
                value
            }
            content_panel__content_panel_color {
                ...kontentColor
            }
            content_panel__content_panel_font_color {
                ...kontentColor
            }
            image_side {
                value {
                    codename
                }
            }
        }
    }
`;
